/**
 * What sending a new link will do to this customer (BON-472).
 *
 * TWO WORDINGS, and the difference is not decoration. The general warning is
 * about a link sitting unread in an inbox. When the customer is in checkout
 * RIGHT NOW the consequence is a different thing entirely — the page they have
 * open stops working mid-flow — and the derived stage already distinguishes it,
 * so there is no excuse for telling them the milder version.
 *
 * Its own file, and its own id, because the dialog names it as its description:
 * this paragraph is the reason the confirmation step exists, and a screen reader
 * that announces only the title never reads it.
 */
import type { ReservationRow } from '../../api/reservations';

import { NO_EMAIL, NO_NAME } from './reservationRotationOutcome';

/** Referenced by the dialog's `aria-describedby`. One resend dialog is open at
 *  a time, so a fixed id is safe and keeps the pairing readable. */
export const RESEND_WARNING_ID = 'resend-reservation-warning';

/** Capitalises the first letter only. The fallbacks are written lower case
 *  because most of their uses are mid-sentence — a single capitalised fallback
 *  is what put "This replaces the link The customer already has" on screen. */
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function ResendReservationWarning({ reservation }: { reservation: ReservationRow }) {
  const inCheckout = reservation.stage === 'engaged';
  const who = reservation.customerName ?? NO_NAME;
  const whoLeading = reservation.customerName ?? sentenceCase(NO_NAME);
  // An address we do not hold used to render as the literal word "null", in a
  // sentence a CSR reads out to the person on the phone.
  const to = reservation.customerEmail ?? NO_EMAIL;

  return (
    <div
      id={RESEND_WARNING_ID}
      className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
    >
      {inCheckout
        ? `${whoLeading} is in checkout right now. Sending a new link cuts them off part-way through — the page they have open stops working, and they have to start again from the new email.`
        : `This replaces the link ${who} already has. The one in their inbox stops working immediately, and a new one is emailed to ${to}.`}
    </div>
  );
}
